import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, Sparkles, FileText, Download, Eye, ArrowRight } from "lucide-react";
import { getShare, downloadSharedDoc } from "@/lib/shareApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MarkdownPreview from "@/components/common/MarkdownPreview";
import ErrorInline from "@/components/common/ErrorInline";

/**
 * Public read-only data-room view rendered at /share/vault/:token.
 * Outside AppShell, no auth required.
 */
export default function PublicVaultPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewMd, setPreviewMd] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);

  const refresh = () => {
    setError(null);
    getShare(token)
      .then(setData)
      .catch((e) => {
        const detail = e?.response?.data?.detail || e?.message || "Couldn't load share";
        setError(detail);
        setData(null);
      });
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const grouped = useMemo(() => {
    if (!data) return [];
    return data.sections.map((s) => ({
      ...s,
      docs: data.docs.filter((d) => d.section_id === s.id),
    }));
  }, [data]);

  const totalDocs = data?.docs?.length ?? 0;

  const handleDownload = async (doc) => {
    try {
      const resp = await downloadSharedDoc(token, doc.id);
      const blob = resp.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const handlePreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewLoading(true);
    try {
      const resp = await downloadSharedDoc(token, doc.id);
      const text = await resp.data.text();
      setPreviewMd(text);
    } catch (e) {
      setPreviewMd(`# Couldn't load preview\n\n${e?.message || ""}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (error) {
    return (
      <div data-testid="share-vault-page" className="mx-auto max-w-3xl px-6 py-20">
        <ErrorInline message={error} />
        <div className="mt-6 text-center text-[12.5px] text-zinc-500">
          The share link may be expired, revoked, or invalid.
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-center text-[13px] text-zinc-500">
        Loading shared data room…
      </div>
    );
  }

  return (
    <div
      data-testid="share-vault-page"
      className="min-h-screen bg-[#F7F7F5] pb-24"
    >
      {/* Top banner */}
      <div className="border-b border-black/[0.04] bg-white/80 px-5 py-4 backdrop-blur sm:px-6 sm:py-5 lg:px-12">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 sm:gap-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
              <Lock className="h-4 w-4" strokeWidth={2.4} />
            </span>
            <div className="min-w-0">
              <div
                data-testid="share-banner-title"
                className="truncate text-[14px] font-extrabold tracking-tight text-zinc-900 sm:text-[16px]"
              >
                {data.company.name} · Investor data room
              </div>
              <div className="truncate text-[11px] text-zinc-500 sm:text-[11.5px]">
                Read-only · Shared by {data.company.name}
              </div>
            </div>
          </div>
          <div className="hidden items-center gap-3 sm:flex">
            <span className="rounded-full border border-black/[0.06] bg-white px-3 py-1 text-[11px] font-semibold text-zinc-700">
              {totalDocs} documents
            </span>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-5 pt-8 sm:px-6 sm:pt-10 lg:px-12">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {grouped.map((s) => (
            <PublicSection
              key={s.id}
              section={s}
              onDownload={handleDownload}
              onPreview={handlePreview}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-16 flex flex-col items-center gap-1 text-center">
          <a
            href="/"
            className="group inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-zinc-800"
          >
            Powered by Idea AI — Run your company like a Fortune 500
            <ArrowRight
              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
              strokeWidth={2.4}
            />
          </a>
          <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-zinc-400">
            This link is read-only. Files cannot be edited.
          </div>
        </div>
      </main>

      {/* Preview dialog */}
      <Dialog
        open={Boolean(previewDoc)}
        onOpenChange={(o) => {
          if (!o) {
            setPreviewDoc(null);
            setPreviewMd("");
          }
        }}
      >
        <DialogContent
          data-testid="share-preview-dialog"
          className="max-h-[80vh] max-w-3xl overflow-hidden p-0"
        >
          <DialogHeader className="border-b border-black/[0.06] bg-white px-6 py-4">
            <DialogTitle className="text-[15px] font-extrabold tracking-tight text-zinc-900">
              {previewDoc?.name ?? "Preview"}
              {previewDoc?.ai_generated && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-violet-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-violet-700">
                  <Sparkles className="h-2.5 w-2.5" strokeWidth={2.6} />
                  AI draft
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto bg-white px-6 py-5">
            {previewLoading ? (
              <div className="text-center text-[12.5px] text-zinc-500">Loading…</div>
            ) : (
              <MarkdownPreview markdown={previewMd} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PublicSection({ section, onDownload, onPreview }) {
  return (
    <section
      data-testid={`share-section-${section.id}`}
      className="card-soft flex flex-col gap-3 rounded-[28px] p-6"
    >
      <header>
        <h2 className="text-[18px] font-extrabold tracking-tight text-zinc-900">
          {section.name}
        </h2>
        <div className="mt-0.5 text-[11.5px] text-zinc-500">
          <span className="font-semibold text-zinc-700">{section.docs.length}</span>{" "}
          {section.docs.length === 1 ? "document" : "documents"} available
        </div>
      </header>

      {section.docs.length === 0 ? (
        <div className="rounded-xl bg-zinc-50/80 px-3 py-4 text-center text-[12px] text-zinc-400">
          No documents in this section yet.
        </div>
      ) : (
        <ul className="flex flex-col gap-1">
          {section.docs.map((d) => (
            <li
              key={d.id}
              data-testid={`share-doc-${d.id}`}
              className="flex items-center gap-3 rounded-xl px-2.5 py-2 hover:bg-zinc-50"
            >
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700">
                <FileText className="h-4 w-4" strokeWidth={2.2} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold text-zinc-900">
                  {d.name}
                  {d.ai_generated && (
                    <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-violet-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-violet-700">
                      <Sparkles className="h-2.5 w-2.5" strokeWidth={2.6} />
                      AI
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-[11px] text-zinc-400">
                  {d.status === "draft" ? "Draft" : "Complete"} · {fmtBytes(d.size_bytes)}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {d.status === "draft" && (
                  <button
                    type="button"
                    data-testid={`share-doc-preview-${d.id}`}
                    onClick={() => onPreview(d)}
                    className="inline-flex items-center gap-1 rounded-full border border-black/[0.06] bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-700 transition-colors hover:bg-zinc-50"
                  >
                    <Eye className="h-3 w-3" strokeWidth={2.6} />
                    Preview
                  </button>
                )}
                <button
                  type="button"
                  data-testid={`share-doc-download-${d.id}`}
                  onClick={() => onDownload(d)}
                  className="inline-flex items-center gap-1 rounded-full border border-black/[0.06] bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-700 transition-colors hover:bg-zinc-50"
                >
                  <Download className="h-3 w-3" strokeWidth={2.6} />
                  Download
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function fmtBytes(b) {
  if (!b) return "—";
  const k = 1024;
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(k)));
  return `${(b / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}
